import { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  FilePlus, Upload, ImageIcon, Sparkles, Plus, Trash2, FileDown, MapPin, FileText,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtZAR = (n) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(Number(n) || 0);

const blankDed = () => ({ date: new Date().toISOString().slice(0, 10), note: '', amount: '' });
const blankTrip = () => ({ date: '', trip_number: '', pods: 0, returns: 0, pay_out: '', fuel: 100 });

// --- TSV/CSV paste helpers (Excel rows → structured data) -----------------
// Cells split on tabs (Excel copy default) OR multi-space if no tabs present.
const _splitRow = (line) => {
  if (line.includes('\t')) return line.split('\t').map(c => c.trim());
  return line.split(/  +|,/).map(c => c.trim());
};

// Parse a ZAR amount: " R16,80 " → 16.80,  "R1,234.50" → 1234.50,  "672,40" → 672.40
const _parseZar = (s) => {
  if (s === null || s === undefined) return 0;
  let t = String(s).replace(/[Rr\s]/g, '').replace(/[^\d,.\-]/g, '');
  if (!t) return 0;
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  if (lastComma > lastDot) {
    // Comma is the decimal separator (South African style)
    t = t.replace(/\./g, '').replace(',', '.');
  } else {
    t = t.replace(/,/g, '');
  }
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 0;
};

// Normalise a date cell: returns YYYY-MM-DD or '' if unparseable
const _parseDate = (s) => {
  if (!s) return '';
  const t = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  // dd/mm/yyyy or dd-mm-yyyy
  const m1 = t.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m1) {
    let [, d, mo, y] = m1;
    if (y.length === 2) y = '20' + y;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  // Excel may send "May 8, 2026" etc — fallback to Date.parse
  const t2 = Date.parse(t);
  if (!Number.isNaN(t2)) {
    const d = new Date(t2);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return '';
};

// Parse Trips pasted from Excel.
// Accepts MULTIPLE Excel layouts:
//   1) Manifest Date \t Trip Number \t Driver Name \t Sum of od rates
//   2) Trip Number \t Driver Name \t Amount  (date inherited from previous row)
//   3) ITT pivot:  Agent Date \t Tripsheet \t Driver \t RATE EX FUEL \t FUEL RATE \t Sum of TOTAL PAY OUT
//      → pay_out = RATE EX FUEL, fuel = FUEL RATE  (totals match the statement format)
// Skips header rows, pivot filter rows ("owner anwill") and "Grand Total" rows.
const parsePastedTrips = (text) => {
  const lines = (text || '').split(/\r?\n/).map(l => l.trimEnd()).filter(l => l.trim().length > 0);
  // Detect ITT pivot layout by scanning all lines for the signature headers
  const allText = lines.join(' \n ').toLowerCase();
  const isITT = (allText.includes('rate ex fu') || allText.includes('rate ex fuel'))
                && allText.includes('fuel rate')
                && (allText.includes('tripsheet') || allText.includes('agent date'));
  const out = [];
  let carriedDate = '';
  for (const line of lines) {
    const cells = _splitRow(line);
    const joined = cells.join(' ').toLowerCase();
    // Skip header / total / pivot-filter rows
    if (joined.includes('grand total')) continue;
    if (joined.includes('manifest date') || joined.includes('sum of od') || joined.includes('trip number')) continue;
    if (joined.includes('agent date') || joined.includes('rate ex fu') || joined.includes('total pay out') || joined.startsWith('owner') || joined.startsWith('tripsheet ')) continue;
    if (cells.length < 2) continue;

    // ---- ITT layout: explicit columns ----
    if (isITT && cells.length >= 5) {
      // Find the trip number (first cell starting with - or 4+ digits/letters)
      let tripIdx = -1, dateIdx = -1, payIdx = -1, fuelIdx = -1, totalIdx = -1, driverIdx = -1;
      for (let i = 0; i < cells.length; i++) {
        if (dateIdx === -1) { const d = _parseDate(cells[i]); if (d) { dateIdx = i; continue; } }
        if (tripIdx === -1 && /^-?\d{4,}$/.test(cells[i].trim())) { tripIdx = i; continue; }
        if (driverIdx === -1 && /[a-zA-Z]{2,}/.test(cells[i]) && !/^\d/.test(cells[i].trim()) && cells[i].trim().toLowerCase() !== '(blank)') { driverIdx = i; continue; }
      }
      // After tripIdx + driverIdx, the remaining 3 numeric-ish cells in order are: rate_ex_fuel, fuel_rate, total_payout
      const numericIdxs = [];
      for (let i = 0; i < cells.length; i++) {
        if (i === dateIdx || i === tripIdx || i === driverIdx) continue;
        const v = _parseZar(cells[i]);
        const raw = (cells[i] || '').trim().toLowerCase();
        // "(blank)" → still occupies a fuel slot but evaluates to 0
        if (v > 0 || raw === '(blank)' || /^[r0-9.,\s()-]+$/i.test(raw)) numericIdxs.push(i);
      }
      if (numericIdxs.length >= 1) {
        payIdx = numericIdxs[0];
        if (numericIdxs.length >= 2) fuelIdx = numericIdxs[1];
        if (numericIdxs.length >= 3) totalIdx = numericIdxs[2];
      }
      if (tripIdx < 0 || (payIdx < 0 && totalIdx < 0)) continue;

      const date = dateIdx >= 0 ? _parseDate(cells[dateIdx]) : '';
      if (date) carriedDate = date;
      const finalDate = date || carriedDate;
      const pay_out = payIdx >= 0 ? _parseZar(cells[payIdx]) : 0;
      const fuel = fuelIdx >= 0 ? _parseZar(cells[fuelIdx]) : 0;
      const totalSeen = totalIdx >= 0 ? _parseZar(cells[totalIdx]) : (pay_out + fuel);
      // If we somehow got pay_out=0 but total>0, fall back to total
      const effectivePay = pay_out > 0 ? pay_out : Math.max(totalSeen - fuel, 0);
      if (effectivePay <= 0 && totalSeen <= 0) continue;
      out.push({
        date: finalDate,
        trip_number: (cells[tripIdx] || '').trim(),
        driver_name: driverIdx >= 0 ? (cells[driverIdx] || '').trim() : '',
        pods: 0,
        returns: 0,
        pay_out: effectivePay,
        fuel,
      });
      continue;
    }

    // ---- Generic layout (existing behaviour) ----
    // Find the amount (last cell that parses to >0)
    let amount = 0;
    let amountIdx = -1;
    for (let i = cells.length - 1; i >= 0; i--) {
      const v = _parseZar(cells[i]);
      if (v > 0) { amount = v; amountIdx = i; break; }
    }
    if (amount <= 0) continue;

    // Find date — first cell that parses as a date
    let date = '';
    let dateIdx = -1;
    for (let i = 0; i < cells.length; i++) {
      const d = _parseDate(cells[i]);
      if (d) { date = d; dateIdx = i; break; }
    }
    if (date) {
      carriedDate = date;
    } else {
      date = carriedDate;
    }

    // Trip number — first non-date / non-amount cell that looks like a code (letters+digits)
    let trip_number = '';
    let tripIdx = -1;
    for (let i = 0; i < cells.length; i++) {
      if (i === dateIdx || i === amountIdx) continue;
      if (/^[A-Z0-9\-_/]{4,}$/i.test(cells[i])) { trip_number = cells[i]; tripIdx = i; break; }
    }
    // Fallback: pick the cell between date and amount that's not the driver name
    if (!trip_number) {
      for (let i = 0; i < cells.length; i++) {
        if (i === dateIdx || i === amountIdx) continue;
        if (cells[i] && !/^[a-z\s]+$/i.test(cells[i])) { trip_number = cells[i]; tripIdx = i; break; }
      }
    }
    // Driver name — first remaining text-only cell (after date/trip/amount)
    let driver_name = '';
    for (let i = 0; i < cells.length; i++) {
      if (i === dateIdx || i === amountIdx || i === tripIdx) continue;
      const v = cells[i] || '';
      if (v && /[a-zA-Z]{2,}/.test(v) && !/^\d/.test(v)) { driver_name = v.trim(); break; }
    }

    out.push({
      date,
      trip_number,
      driver_name,
      pods: 0,
      returns: 0,
      pay_out: amount,
      fuel: 0,
    });
  }
  return out;
};

// Parse Deduction rows pasted from Excel.
// kind: 'uniform' | 'iou' | 'fuel' | 'git'
// Returns array of {date, note, amount}. For uniform, packs Name|Item Size xQty into note and uses Total (last numeric column) as amount.
const parsePastedDeductions = (text, kind) => {
  const lines = (text || '').split(/\r?\n/).map(l => l.trimEnd()).filter(l => l.trim().length > 0);
  const out = [];
  for (const line of lines) {
    const cells = _splitRow(line);
    const joined = cells.join(' ').toLowerCase();
    if (joined.includes('grand total')) continue;
    if (joined.includes('date') && joined.includes('amount')) continue;  // header
    if (cells.length < 2) continue;

    // Find amount (last cell with numeric value > 0)
    let amount = 0; let amountIdx = -1;
    for (let i = cells.length - 1; i >= 0; i--) {
      const v = _parseZar(cells[i]);
      if (v > 0) { amount = v; amountIdx = i; break; }
    }
    if (amount <= 0) continue;

    // Date = first parseable date cell
    let date = ''; let dateIdx = -1;
    for (let i = 0; i < cells.length; i++) {
      const d = _parseDate(cells[i]);
      if (d) { date = d; dateIdx = i; break; }
    }
    if (!date) date = new Date().toISOString().slice(0, 10);

    // Build note from remaining cells
    const notePieces = cells
      .map((c, i) => ({ c, i }))
      .filter(({ i }) => i !== dateIdx && i !== amountIdx)
      .map(({ c }) => c.trim())
      .filter(Boolean);
    let note = notePieces.join(' | ');

    if (kind === 'uniform') {
      // Excel cols: Date, Name, Item, Size, Quantity, Amount, Total
      // notePieces order (after stripping date+amount): Name, Item, Size, Quantity, Amount
      const [nm, item, size, qty, unitAmt] = notePieces;
      const parts = [];
      if (nm) parts.push(nm);
      const itemBits = [item, size].filter(Boolean).join(' ');
      if (itemBits) parts.push(itemBits);
      if (qty && unitAmt) parts.push(`${qty} × ${unitAmt}`);
      else if (qty) parts.push(`Qty ${qty}`);
      note = parts.join(' | ');
    }

    out.push({ date, note, amount });
  }
  return out;
};
// --------------------------------------------------------------------------

export default function GenerateStatements() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [drivers, setDrivers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState('');
  const [subjectKind, setSubjectKind] = useState('driver');  // 'driver' | 'employee'

  // Statement form
  const [driverId, setDriverId] = useState('');
  const [driverName, setDriverName] = useState('');
  const [siteId, setSiteId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [trips, setTrips] = useState([]);
  const [git, setGit] = useState([]);
  const [iou, setIou] = useState([]);
  const [fuelDeducts, setFuelDeducts] = useState([]);
  const [uniform, setUniform] = useState([]);
  const [footerNote, setFooterNote] = useState('A standard GIT and Insurance Third Party Claim Fee has been deducted from this statement.');
  const [notes, setNotes] = useState('');

  // OCR
  const [ocrBusy, setOcrBusy] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const dropRef = useRef(null);
  const fileRef = useRef(null);

  // Last result
  const [lastStmt, setLastStmt] = useState(null);

  // Excel-paste UI state
  const [showTripPaste, setShowTripPaste] = useState(false);
  const [tripPasteText, setTripPasteText] = useState('');
  const [pasteOpen, setPasteOpen] = useState({});   // {git:bool, iou:bool, fuel:bool, uniform:bool}
  const [pasteText, setPasteText] = useState({});   // per-tab text

  // Owner dropdown + bulk-owner mode
  const [owners, setOwners] = useState([]);    // [{owner, driver_count, drivers:[]}]
  const [ownerFilter, setOwnerFilter] = useState('');   // for filtering the subject dropdown
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkOwner, setBulkOwner] = useState('');
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  // Once-off subject (no driver record needed)
  const [onceOff, setOnceOff] = useState(false);  // toggled on -> driverId set to ONCE-OFF, name freeform

  // Deductions Ledger — pending open entries for the selected subject + period
  const [ledgerPending, setLedgerPending] = useState({ entries: [], count: 0, total: 0 });
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/statements-advanced/owners`, { headers })
      .then(r => setOwners(r.data?.owners || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [dRes, sRes, eRes] = await Promise.all([
          axios.get(`${API_URL}/drivers`, { headers }),
          axios.get(`${API_URL}/sites`, { headers }),
          axios.get(`${API_URL}/internal-staff`, { headers }).catch(() => ({ data: [] })),
        ]);
        setDrivers(dRes.data || []);
        setSites(sRes.data || []);
        setStaff(eRes.data || []);
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to load page'));
      }
    })();
  }, []);  // eslint-disable-line

  // Build a unified subject list (owner-drivers + employees) tagged by kind
  const subjects = subjectKind === 'driver'
    ? (drivers || []).map(d => ({
        id: d.id,
        label: d.driver_name || d.full_name || d.email,
        display_name: d.driver_name || d.full_name || d.email,
        allocated_site_id: d.allocated_site_id || '',
        allocated_site_name: d.allocated_site_name || '',
        sub: d.allocated_site_name ? `${d.allocated_site_name}` : '',
      }))
    : (staff || []).map(s => ({
        id: s.id,
        label: s.full_name || s.email,
        display_name: s.full_name || s.email,
        allocated_site_id: '',
        allocated_site_name: '',
        sub: s.position || '',
      }));

  const filteredSubjects = (() => {
    let arr = subjects;
    if (subjectKind === 'driver' && siteFilter) {
      arr = arr.filter(x => x.allocated_site_id === siteFilter);
    }
    if (subjectKind === 'driver' && ownerFilter) {
      const _norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
      // Tokenise into first / last name word sets so "Wayde Thomas" matches "WAYDE PETER THOMAS"
      const _tokens = (s) => new Set(_norm(s).split(' ').filter(w => w.length > 1));
      const bucket = owners.find(o => _norm(o.owner) === _norm(ownerFilter));
      if (bucket) {
        const bucketDriverTokenSets = (bucket.drivers || []).map(d => _tokens(d.name));
        arr = arr.filter(x => {
          const sub = _tokens(x.display_name || x.label || '');
          if (!sub.size) return false;
          // Match: bucket driver shares ≥ 2 word tokens with the subject (first + last name)
          return bucketDriverTokenSets.some(bd => {
            let overlap = 0;
            for (const w of sub) if (bd.has(w)) overlap++;
            return overlap >= 2 || (overlap === 1 && (bd.size === 1 || sub.size === 1));
          });
        });
      }
    }
    return arr;
  })();

  // Derived: is the selected site DPD Laser? (used to hide PODs/Returns/Fuel)
  const selectedSite = sites.find(s => s.id === siteId);
  const isDpdLaser = (() => {
    const n = (selectedSite?.name || '').toLowerCase();
    return n.includes('dpd') && n.includes('laser');
  })();

  // ----- OCR helpers -----
  const runOCR = async (b64, mime) => {
    setOcrBusy(true);
    try {
      const res = await axios.post(`${API_URL}/statements-advanced/ocr-image`,
        { image_base64: b64, mime_type: mime || 'image/png' },
        { headers, timeout: 120000 });
      const rows = (res.data?.rows || []).map(r => ({
        date: r.date || '',
        trip_number: r.trip_number || '',
        pods: 0,
        returns: 0,
        pay_out: Number(r.amount) || 0,
        fuel: 100,           // user-tunable
        _src_driver: r.driver_name || '',  // informational
      }));
      // Auto-set period if rows include dates
      const dates = rows.map(r => r.date).filter(Boolean).sort();
      if (dates.length) {
        if (!periodStart) setPeriodStart(dates[0]);
        if (!periodEnd) setPeriodEnd(dates[dates.length - 1]);
      }
      // Auto-fill driver name if all rows share one
      const uniqueDrivers = [...new Set(rows.map(r => r._src_driver).filter(Boolean))];
      if (uniqueDrivers.length === 1 && !driverName) setDriverName(uniqueDrivers[0]);
      // Append (don't replace) — clerk may paste several screenshots
      setTrips(prev => [...prev, ...rows]);
      toast.success(`Extracted ${rows.length} trip${rows.length === 1 ? '' : 's'} from image`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'OCR failed'));
    } finally {
      setOcrBusy(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Use a PNG / JPEG / WEBP image');
      return;
    }
    // Read + auto-resize to keep payload manageable (max edge 1800px, jpeg 85)
    const reader = new FileReader();
    reader.onload = async (e) => {
      const originalDataUrl = e.target.result;
      setImagePreview(originalDataUrl);
      let dataUrlToSend = originalDataUrl;
      try {
        if (file.size > 1_500_000) {
          dataUrlToSend = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              const MAX = 1800;
              const scale = Math.min(1, MAX / Math.max(img.width, img.height));
              const w = Math.round(img.width * scale);
              const h = Math.round(img.height * scale);
              const canvas = document.createElement('canvas');
              canvas.width = w; canvas.height = h;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, w, h);
              resolve(canvas.toDataURL('image/jpeg', 0.85));
            };
            img.onerror = reject;
            img.src = originalDataUrl;
          });
        }
      } catch { /* fall back to original */ }
      const [header, b64] = dataUrlToSend.split(',');
      const mime = header.startsWith('data:image/jpeg') ? 'image/jpeg' : file.type;
      runOCR(b64, mime);
    };
    reader.readAsDataURL(file);
  };

  // Paste from clipboard
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            handleFile(f);
            return;
          }
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);  // eslint-disable-line

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // ----- Manual trip + deduction edit helpers -----
  const updateTrip = (i, patch) => setTrips(prev => prev.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const addTrip = () => setTrips(prev => [...prev, blankTrip()]);
  const removeTrip = (i) => setTrips(prev => prev.filter((_, idx) => idx !== i));

  const mkBucket = (state, setState) => ({
    add: () => setState([...state, blankDed()]),
    update: (i, patch) => setState(state.map((d, idx) => idx === i ? { ...d, ...patch } : d)),
    remove: (i) => setState(state.filter((_, idx) => idx !== i)),
    total: state.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0),
  });
  const gitB = mkBucket(git, setGit);
  const iouB = mkBucket(iou, setIou);
  const fuelB = mkBucket(fuelDeducts, setFuelDeducts);
  const uniformB = mkBucket(uniform, setUniform);

  const totalPods = trips.reduce((s, t) => s + (parseInt(t.pods, 10) || 0), 0);
  const totalReturns = trips.reduce((s, t) => s + (parseInt(t.returns, 10) || 0), 0);
  const totalPayOut = trips.reduce((s, t) => s + (parseFloat(t.pay_out) || 0), 0);
  const totalFuel = trips.reduce((s, t) => s + (parseFloat(t.fuel) || 0), 0);
  const totalDeductions = gitB.total + iouB.total + fuelB.total + uniformB.total;
  const finalAmount = totalPayOut + totalFuel - totalDeductions;

  const handleGenerate = async () => {
    if (!driverId) { toast.error('Pick a driver'); return; }
    if (!trips.length) { toast.error('Add at least one trip'); return; }
    if (!periodStart || !periodEnd) { toast.error('Set the statement period'); return; }
    const cleanDeds = (arr) => arr.filter(d => (parseFloat(d.amount) || 0) > 0)
      .map(d => ({ date: d.date || null, note: d.note || null, amount: parseFloat(d.amount) || 0 }));
    try {
      const res = await axios.post(`${API_URL}/statements-advanced/generate`, {
        driver_id: driverId,
        driver_name: driverName || null,
        subject_kind: subjectKind,
        site_id: siteId || null,
        owner_name: ownerFilter || null,
        period_start: periodStart,
        period_end: periodEnd,
        trips: trips.map(t => ({
          date: t.date || '',
          trip_number: t.trip_number || '',
          pods: parseInt(t.pods, 10) || 0,
          returns: parseInt(t.returns, 10) || 0,
          pay_out: parseFloat(t.pay_out) || 0,
          fuel: parseFloat(t.fuel) || 0,
        })),
        git_entries: cleanDeds(git),
        iou_entries: cleanDeds(iou),
        fuel_entries: cleanDeds(fuelDeducts),
        uniform_entries: cleanDeds(uniform),
        footer_note: footerNote || null,
        notes: notes || null,
      }, { headers });
      toast.success('Statement generated');
      setLastStmt(res.data);

      // Apply ledger-linked deduction lines so they flip from open → applied
      const appliedIds = [
        ...iou.map(d => d._ledger_id),
        ...fuelDeducts.map(d => d._ledger_id),
        ...uniform.map(d => d._ledger_id),
      ].filter(Boolean);
      if (appliedIds.length && res.data?.id) {
        try {
          await axios.post(`${API_URL}/deductions-ledger/apply`, {
            entry_ids: appliedIds,
            statement_id: res.data.id,
          }, { headers });
        } catch (e) {
          console.warn('apply ledger entries failed', e);
        }
        // Refresh ledger so the badge updates
        setTimeout(() => fetchLedgerPending(), 200);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate statement'));
    }
  };

  // ---- Deductions Ledger: pull pending open entries for the selected subject ----
  const fetchLedgerPending = useCallback(async () => {
    if (!driverName || !periodStart || !periodEnd) {
      setLedgerPending({ entries: [], count: 0, total: 0 });
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/deductions-ledger/entries`, {
        headers,
        params: {
          employee: driverName,
          status: 'open',
          date_from: periodStart,
          date_to: periodEnd,
        },
      });
      setLedgerPending({
        entries: res.data?.entries || [],
        count: res.data?.count || 0,
        total: res.data?.total || 0,
      });
    } catch (err) {
      console.warn('fetchLedgerPending failed', err);
      setLedgerPending({ entries: [], count: 0, total: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName, periodStart, periodEnd]);

  useEffect(() => { fetchLedgerPending(); }, [fetchLedgerPending]);

  // Merge the pending ledger entries into the relevant deduction buckets.
  // Each line is tagged with `_ledger_id` so we can mark it `applied` after generate.
  const pullFromLedger = async () => {
    if (!ledgerPending.entries.length) {
      toast.info('No open ledger entries for this name + period');
      return;
    }
    setLedgerLoading(true);
    try {
      const buckets = { iou: [], fuel: [], uniform: [] };
      for (const e of ledgerPending.entries) {
        if (!buckets[e.kind]) continue;
        const note = e.kind === 'uniform'
          ? [e.item, e.size && `(${e.size})`, (e.quantity || 1) > 1 && `× ${e.quantity}`].filter(Boolean).join(' ') || (e.description || 'Uniform')
          : (e.description || (e.kind === 'iou' ? 'Loan' : 'Fuel'));
        buckets[e.kind].push({
          date: e.date || '',
          note,
          amount: e.amount,
          _ledger_id: e.id,
        });
      }
      // Avoid double-merging entries that are already in the bucket
      const dedupe = (existing, incoming) => {
        const seen = new Set(existing.map(d => d._ledger_id).filter(Boolean));
        return [...existing, ...incoming.filter(d => !seen.has(d._ledger_id))];
      };
      setIou(prev => dedupe(prev, buckets.iou));
      setFuelDeducts(prev => dedupe(prev, buckets.fuel));
      setUniform(prev => dedupe(prev, buckets.uniform));
      toast.success(`Pulled ${ledgerPending.count} ledger entr${ledgerPending.count === 1 ? 'y' : 'ies'}`);
    } finally {
      setLedgerLoading(false);
    }
  };

  // ---- Bulk Owner: paste rows → one statement per driver ----
  const handleBulkOwner = async () => {
    if (!bulkOwner) { toast.error('Pick an owner first'); return; }
    if (!periodStart || !periodEnd) { toast.error('Set the statement period'); return; }
    const parsed = parsePastedTrips(bulkPasteText);
    if (!parsed.length) { toast.error('No trip rows recognised in the paste box'); return; }
    const driverNames = [...new Set(parsed.map(r => (r.driver_name || '').trim()).filter(Boolean))];
    if (!driverNames.length) {
      toast.error('No DRIVER NAME column found in pasted data — make sure your Excel paste includes a driver name column.');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/statements-advanced/generate-bulk-owner`, {
        owner_name: bulkOwner,
        site_id: siteId || null,
        site_name: selectedSite?.name || null,
        period_start: periodStart,
        period_end: periodEnd,
        rows: parsed.map(r => ({
          date: r.date,
          trip_number: r.trip_number,
          driver_name: r.driver_name,
          amount: r.pay_out,
        })),
        footer_note: footerNote || null,
        notes: notes || null,
      }, { headers });
      setBulkResult(res.data);
      toast.success(`Generated ${res.data.created_count} statement${res.data.created_count === 1 ? '' : 's'} for ${bulkOwner}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Bulk-owner generation failed'));
    }
  };

  const downloadPDF = async (id, name) => {
    try {
      const res = await axios.get(`${API_URL}/statements-advanced/${id}/pdf`,
        { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `JSM_Statement_${name || 'driver'}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  // ---- Deduction tab body ----
  const DeductionPanel = ({ label, bucket, state, setState, color, testid, kind, columns }) => {
    const open = !!pasteOpen[testid];
    const text = pasteText[testid] || '';
    const previewCount = parsePastedDeductions(text, kind).length;
    return (
      <div className="space-y-2" data-testid={`ded-panel-${testid}`}>
        <div className="flex items-center justify-between">
          <Label className={`text-sm font-bold ${color}`}>{label} entries</Label>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setPasteOpen(p => ({ ...p, [testid]: !p[testid] }))} className="h-7" data-testid={`ded-paste-toggle-${testid}`}>
              <Upload className="h-3 w-3 mr-1" /> {open ? 'Hide paste' : 'Paste from Excel'}
            </Button>
            <Button type="button" size="sm" onClick={bucket.add} className="h-7" data-testid={`ded-add-${testid}`}>
              <Plus className="h-3 w-3 mr-1" /> Add line
            </Button>
          </div>
        </div>
        {open && (
          <div className={`mb-1 space-y-2 border border-dashed rounded p-2 ${color.replace('text-', 'border-')}/60`} data-testid={`ded-paste-box-${testid}`}>
            <div className="text-[11px]">
              <b>Paste rows from Excel</b> (tab-separated). Expected: <code>{columns}</code>
            </div>
            <Textarea
              value={text}
              onChange={(e) => setPasteText(p => ({ ...p, [testid]: e.target.value }))}
              rows={4}
              placeholder={columns}
              className="text-xs font-mono"
              data-testid={`ded-paste-textarea-${testid}`}
            />
            <div className="flex gap-2">
              <Button
                type="button" size="sm"
                onClick={() => {
                  const parsed = parsePastedDeductions(text, kind);
                  if (!parsed.length) { toast.error('No rows recognised — check the columns / amounts'); return; }
                  setState([...state, ...parsed]);
                  setPasteText(p => ({ ...p, [testid]: '' }));
                  setPasteOpen(p => ({ ...p, [testid]: false }));
                  toast.success(`Added ${parsed.length} ${label.toLowerCase()} ${parsed.length === 1 ? 'row' : 'rows'}`);
                }}
                data-testid={`ded-paste-import-${testid}`}
              ><Sparkles className="h-3 w-3 mr-1" /> Import {previewCount || ''} rows</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => { setPasteText(p => ({ ...p, [testid]: '' })); setPasteOpen(p => ({ ...p, [testid]: false })); }}>Cancel</Button>
            </div>
          </div>
        )}
        {state.length === 0 ? (
          <div className="text-xs text-muted-foreground py-3 text-center border border-dashed rounded">
            No {label} deductions yet.
          </div>
        ) : (
          <div className="space-y-1">
            {state.map((d, idx) => (
              <div key={idx} className="grid grid-cols-[110px_1fr_120px_28px] gap-2 items-center">
                <Input type="date" value={d.date} onChange={(e) => bucket.update(idx, { date: e.target.value })} className="h-8 text-xs" />
                <Input value={d.note} onChange={(e) => bucket.update(idx, { note: e.target.value })} placeholder="Note (e.g. reason / ref)" className="h-8 text-xs" data-testid={`ded-note-${testid}-${idx}`} />
                <Input
                  type="text"
                  inputMode="decimal"
                  value={d.amount === 0 || d.amount === '0' ? '' : (d.amount ?? '')}
                  onChange={(e) => {
                    // Allow free typing: digits, one dot OR comma. Strip everything else.
                    const raw = (e.target.value || '').replace(/[^\d.,]/g, '').replace(',', '.');
                    // Block multiple decimal separators
                    const parts = raw.split('.');
                    const cleaned = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : raw;
                    bucket.update(idx, { amount: cleaned });
                  }}
                  placeholder="e.g. 399"
                  className="h-8 text-xs text-right font-mono"
                  data-testid={`ded-amount-${testid}-${idx}`}
                />
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => bucket.remove(idx)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="text-right text-sm font-semibold">
          Sub-total: <span className={color}>- {fmtZAR(bucket.total)}</span>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="generate-statements">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Generate Driver Statement</h1>
            <p className="text-muted-foreground text-sm">Paste a manifest screenshot, review, add deductions, generate PDF.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <Select value={siteFilter} onValueChange={(v) => setSiteFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-8 w-56" data-testid="site-filter"><SelectValue placeholder="Filter drivers by site..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sites</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={ownerFilter || '__any__'} onValueChange={(v) => setOwnerFilter(v === '__any__' ? '' : v)}>
              <SelectTrigger className="h-8 w-60" data-testid="owner-filter"><SelectValue placeholder="Filter by owner..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">All owners</SelectItem>
                {owners.map(o => (
                  <SelectItem key={o.owner} value={o.owner}>{o.owner} ({o.driver_count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant={onceOff ? 'default' : 'outline'}
              onClick={() => {
                if (onceOff) {
                  setOnceOff(false); setDriverId(''); setDriverName('');
                } else {
                  setOnceOff(true);
                  setDriverId(`ONCE-OFF-${Date.now()}`);
                  setDriverName('');
                  toast.message('Once-off mode: type any subject name below');
                }
              }}
              data-testid="onceoff-toggle"
            >{onceOff ? '✓ Once-off' : 'Once-off'}</Button>
            <Button
              type="button"
              size="sm"
              variant={bulkMode ? 'default' : 'outline'}
              onClick={() => setBulkMode(b => !b)}
              data-testid="bulk-mode-toggle"
            >{bulkMode ? '✓ Bulk Owner' : 'Bulk Owner'}</Button>
          </div>
        </div>

        {bulkMode && (
          <Card className="border-violet-400/60 bg-violet-50/40" data-testid="bulk-owner-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-violet-900 flex items-center gap-2">
                <Upload className="h-4 w-4" /> Bulk Owner Statement Pack
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Paste all trip rows for the owner's drivers in one go (must include the DRIVER NAME column).
                Each driver gets their own statement under the owner's profile.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-[1fr_180px_180px] gap-2">
                <Select value={bulkOwner || '__none__'} onValueChange={(v) => { setBulkOwner(v === '__none__' ? '' : v); setBulkResult(null); }}>
                  <SelectTrigger className="h-9" data-testid="bulk-owner-select"><SelectValue placeholder="Pick the OWNER..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Choose owner —</SelectItem>
                    {owners.map(o => (
                      <SelectItem key={o.owner} value={o.owner}>{o.owner} ({o.driver_count} drivers)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="h-9" data-testid="bulk-period-start" />
                <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="h-9" data-testid="bulk-period-end" />
              </div>
              <Textarea
                value={bulkPasteText}
                onChange={(e) => setBulkPasteText(e.target.value)}
                rows={6}
                placeholder={"Manifest Date\tTrip Number\tDriver Name\tAmount\n2026-05-08\tCPT2009834\tWAYDE PETER THOMAS\tR1500,50\n2026-05-08\tCPT2009835\tWAYDE PETER THOMAS\tR900,00\n2026-05-09\tCPT2009836\tTHERLO MIKIAL JULIE\tR750,25"}
                className="text-xs font-mono"
                data-testid="bulk-paste-textarea"
              />
              <div className="text-xs text-violet-700">
                Drivers detected:&nbsp;
                <strong>{[...new Set(parsePastedTrips(bulkPasteText).map(r => r.driver_name).filter(Boolean))].length}</strong>
                {' '}({[...new Set(parsePastedTrips(bulkPasteText).map(r => r.driver_name).filter(Boolean))].join(', ') || 'none yet'})
              </div>
                  <Button onClick={handleBulkOwner} className="bg-violet-700 hover:bg-violet-800" data-testid="bulk-generate-btn">
                    <Sparkles className="h-3 w-3 mr-1" /> Generate {parsePastedTrips(bulkPasteText).length || ''} rows → 1 statement per driver
                  </Button>
                  {bulkResult && bulkResult.owner_name === bulkOwner && (
                <div className="bg-white border border-violet-200 rounded p-3 space-y-2" data-testid="bulk-result">
                  <p className="text-sm font-semibold text-violet-900">
                    Created {bulkResult.created_count} statement{bulkResult.created_count === 1 ? '' : 's'} for {bulkResult.owner_name}
                  </p>
                  <div className="space-y-1">
                    {(bulkResult.statements || []).map(s => (
                      <div key={s.id} className="flex items-center justify-between text-xs bg-violet-50 px-2 py-1 rounded">
                        <span>{s.driver_name} — {s.trips} trip{s.trips === 1 ? '' : 's'} — <strong>R {s.final_amount.toFixed(2)}</strong></span>
                        <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => downloadPDF(s.id, s.driver_name)} data-testid={`bulk-pdf-${s.id}`}>
                          <FileText className="h-3 w-3 mr-1" /> PDF
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
          {/* LEFT: form */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FilePlus className="h-4 w-4" /> Driver, Site &amp; Period
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-[120px_1fr_1fr] gap-3 items-end">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={subjectKind} onValueChange={(v) => { setSubjectKind(v); setDriverId(''); setDriverName(''); setSiteId(''); }}>
                      <SelectTrigger className="h-9" data-testid="subject-kind-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="driver">Owner Driver</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{subjectKind === 'driver' ? 'Driver' : 'Employee'} *</Label>
                    <Select value={driverId} onValueChange={(v) => {
                      setDriverId(v);
                      const x = subjects.find(z => z.id === v);
                      if (x) {
                        setDriverName(x.display_name || '');
                        if (x.allocated_site_id) setSiteId(x.allocated_site_id);
                      }
                    }}>
                      <SelectTrigger className="h-9" data-testid="driver-select"><SelectValue placeholder={`Pick a ${subjectKind === 'driver' ? 'driver' : 'employee'}`} /></SelectTrigger>
                      <SelectContent>
                        {filteredSubjects.length === 0 ? (
                          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                            {subjectKind === 'driver' ? `No owner-drivers found${siteFilter ? ' for this site' : ''}.` : 'No employees found.'}
                          </div>
                        ) : filteredSubjects.map(x => (
                          <SelectItem key={x.id} value={x.id}>
                            {x.label}{x.sub ? ` — ${x.sub}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Site</Label>
                    <Select value={siteId || '__none__'} onValueChange={(v) => setSiteId(v === '__none__' ? '' : v)}>
                      <SelectTrigger className="h-9" data-testid="site-select"><SelectValue placeholder="No site" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Driver Name on Statement</Label>
                  <Input value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="Auto-filled from driver" className="h-9" data-testid="driver-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Period Start *</Label>
                    <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="h-9" data-testid="period-start" />
                  </div>
                  <div>
                    <Label className="text-xs">Period End *</Label>
                    <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="h-9" data-testid="period-end" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-violet-500" /> Paste / Upload Manifest Image
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  ref={dropRef}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  onClick={() => !ocrBusy && fileRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition ${ocrBusy ? 'border-amber-300 bg-amber-50 cursor-progress' : 'border-violet-300 cursor-pointer hover:bg-violet-50'}`}
                  data-testid="ocr-dropzone"
                >
                  <ImageIcon className={`h-8 w-8 mx-auto mb-2 ${ocrBusy ? 'text-amber-500 animate-pulse' : 'text-violet-500'}`} />
                  <div className="text-sm font-medium">
                    {ocrBusy ? 'Reading image with Gemini 2.5 Pro... (usually 15-40s)' : 'Click to upload, drop a file, or paste from clipboard'}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {ocrBusy ? 'If this hangs > 60s, click Cancel and try a smaller / cropped screenshot.' : 'PNG / JPEG / WEBP — trips append to the table below.'}
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="ocr-file-input" />
                  {ocrBusy && (
                    <Button type="button" variant="outline" size="sm" className="mt-3" onClick={(e) => { e.stopPropagation(); setOcrBusy(false); toast.message('Cancelled. Click again to try a smaller image.'); }} data-testid="ocr-cancel-btn">
                      Cancel
                    </Button>
                  )}
                </div>
                {imagePreview && (
                  <div className="mt-3 max-h-40 overflow-hidden rounded border">
                    <img src={imagePreview} alt="manifest preview" className="w-full object-contain" />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Trips ({trips.length})</CardTitle>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowTripPaste(s => !s)} className="h-7" data-testid="trips-toggle-paste">
                    <Upload className="h-3 w-3 mr-1" /> {showTripPaste ? 'Hide paste' : 'Paste from Excel'}
                  </Button>
                  <Button type="button" size="sm" onClick={addTrip} className="h-7" data-testid="trips-add-row"><Plus className="h-3 w-3 mr-1" />Add row</Button>
                </div>
              </CardHeader>
              <CardContent>
                {showTripPaste && (
                  <div className="mb-3 space-y-2 bg-violet-50 border border-violet-200 rounded p-3" data-testid="trip-paste-box">
                    <div className="text-xs text-violet-900">
                      <b>Paste rows from Excel</b> (tab-separated). Header rows, pivot filters and "Grand Total" rows are ignored.
                      <br />Supported layouts:
                      <br />• <code>Manifest Date / Trip Number / Driver Name / Sum of od rates</code>
                      <br />• <code>Agent Date / Tripsheet / Driver / RATE EX FUEL / FUEL RATE / Sum of TOTAL PAY OUT</code> (ITT pivot)
                    </div>
                    <Textarea
                      value={tripPasteText}
                      onChange={(e) => setTripPasteText(e.target.value)}
                      rows={5}
                      placeholder={"2026-05-08\tCPT2009834\tDANIEL MORKEL\tR16,80\n\tCPT2009836\tDANIEL MORKEL\tR672,40"}
                      className="text-xs font-mono"
                      data-testid="trip-paste-textarea"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button" size="sm"
                        onClick={() => {
                          const parsed = parsePastedTrips(tripPasteText);
                          if (!parsed.length) { toast.error('No trip rows recognised — check the columns / amounts'); return; }
                          // Auto-select driver from pasted DRIVER NAME column if not yet picked.
                          // If a driver IS selected, filter out rows for other drivers.
                          const currentName = (driverName || '').trim().toLowerCase();
                          let added = parsed;
                          let ignored = 0;
                          if (!driverId && parsed.length) {
                            // Pick the first row's driver_name and try to match to a known subject
                            const firstDn = (parsed[0].driver_name || '').trim().toLowerCase();
                            if (firstDn) {
                              const match = subjects.find(s => (s.display_name || '').toLowerCase() === firstDn);
                              if (match) {
                                setDriverId(match.id);
                                setDriverName(match.display_name);
                                toast.success(`Auto-selected: ${match.display_name}`);
                              }
                            }
                          } else if (currentName) {
                            const before = added.length;
                            added = added.filter(r => !r.driver_name || r.driver_name.trim().toLowerCase() === currentName);
                            ignored = before - added.length;
                          }
                          // Strip the driver_name field (not part of the trip schema)
                          const clean = added.map(({ driver_name, ...rest }) => rest);
                          setTrips(prev => [...prev, ...clean]);
                          setTripPasteText(''); setShowTripPaste(false);
                          if (ignored > 0) {
                            toast.message(`Added ${clean.length} rows — ${ignored} ignored (other drivers)`);
                          } else {
                            toast.success(`Added ${clean.length} trip ${clean.length === 1 ? 'row' : 'rows'}`);
                          }
                        }}
                        data-testid="trip-paste-import-btn"
                      ><Sparkles className="h-3 w-3 mr-1" /> Import {parsePastedTrips(tripPasteText).length || ''} rows</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => { setTripPasteText(''); setShowTripPaste(false); }}>Cancel</Button>
                    </div>
                  </div>
                )}
                {trips.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-6">No trips yet — paste a manifest or click <i>Add row</i>.</div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-32">Date</TableHead>
                          <TableHead className="w-36">Trip #</TableHead>
                          {!isDpdLaser && <TableHead className="w-16 text-center">PODs</TableHead>}
                          {!isDpdLaser && <TableHead className="w-20 text-center">Returns</TableHead>}
                          <TableHead className="text-right">{isDpdLaser ? 'Amount' : 'Pay Out'}</TableHead>
                          {!isDpdLaser && <TableHead className="text-right w-24">Fuel</TableHead>}
                          <TableHead className="w-8" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trips.map((t, i) => (
                          <TableRow key={i} data-testid={`trip-row-${i}`}>
                            <TableCell><Input type="date" value={t.date} onChange={(e) => updateTrip(i, { date: e.target.value })} className="h-7 text-xs" /></TableCell>
                            <TableCell><Input value={t.trip_number} onChange={(e) => updateTrip(i, { trip_number: e.target.value })} className="h-7 text-xs" /></TableCell>
                            {!isDpdLaser && <TableCell><Input type="number" value={t.pods} onChange={(e) => updateTrip(i, { pods: e.target.value })} className="h-7 text-xs text-center" /></TableCell>}
                            {!isDpdLaser && <TableCell><Input type="number" value={t.returns} onChange={(e) => updateTrip(i, { returns: e.target.value })} className="h-7 text-xs text-center" /></TableCell>}
                            <TableCell>
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={t.pay_out === 0 || t.pay_out === '0' ? '' : (t.pay_out ?? '')}
                                onChange={(e) => {
                                  const raw = (e.target.value || '').replace(/[^\d.,]/g, '').replace(',', '.');
                                  const parts = raw.split('.');
                                  const cleaned = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : raw;
                                  updateTrip(i, { pay_out: cleaned });
                                }}
                                placeholder="e.g. 1500"
                                className="h-7 text-xs text-right font-mono"
                              />
                            </TableCell>
                            {!isDpdLaser && (
                              <TableCell>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  value={t.fuel === 0 || t.fuel === '0' ? '' : (t.fuel ?? '')}
                                  onChange={(e) => {
                                    const raw = (e.target.value || '').replace(/[^\d.,]/g, '').replace(',', '.');
                                    const parts = raw.split('.');
                                    const cleaned = parts.length > 1 ? parts[0] + '.' + parts.slice(1).join('') : raw;
                                    updateTrip(i, { fuel: cleaned });
                                  }}
                                  placeholder="e.g. 100"
                                  className="h-7 text-xs text-right font-mono"
                                />
                              </TableCell>
                            )}
                            <TableCell><Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600" onClick={() => removeTrip(i)}><Trash2 className="h-3 w-3" /></Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {trips.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-3 border-t mt-3 text-xs">
                    <div>Total PODs: <b>{totalPods}</b></div>
                    <div>Total Returns: <b>{totalReturns}</b></div>
                    <div>Total Pay Out: <b>{fmtZAR(totalPayOut)}</b></div>
                    <div>Total Fuel: <b>{fmtZAR(totalFuel)}</b></div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">Deductions</CardTitle>
                {driverName && periodStart && periodEnd && (
                  <div className="flex items-center gap-2 text-xs">
                    {ledgerPending.count > 0 ? (
                      <span className="text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-1" data-testid="ledger-pending-badge">
                        <b>{ledgerPending.count}</b> open ledger {ledgerPending.count === 1 ? 'entry' : 'entries'} · <b>{fmtZAR(ledgerPending.total)}</b>
                      </span>
                    ) : (
                      <span className="text-muted-foreground" data-testid="ledger-pending-badge">No open ledger entries</span>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={pullFromLedger}
                      disabled={ledgerLoading || ledgerPending.count === 0}
                      className="h-7"
                      data-testid="pull-from-ledger-btn"
                    >
                      {ledgerLoading ? 'Pulling…' : 'Pull from Ledger'}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="git">
                  <TabsList>
                    <TabsTrigger value="git" data-testid="tab-git">GIT</TabsTrigger>
                    <TabsTrigger value="iou" data-testid="tab-iou">IOU</TabsTrigger>
                    {!isDpdLaser && <TabsTrigger value="fuel" data-testid="tab-fuel">Fuel</TabsTrigger>}
                    <TabsTrigger value="uniform" data-testid="tab-uniform">Uniform</TabsTrigger>
                  </TabsList>
                  <TabsContent value="git">       <DeductionPanel label="GIT / Insurance"  bucket={gitB}     state={git}          setState={setGit}          color="text-red-600"    testid="git"     kind="git"     columns="Date  Description  Amount" /></TabsContent>
                  <TabsContent value="iou">       <DeductionPanel label="IOU"               bucket={iouB}     state={iou}          setState={setIou}          color="text-amber-700"  testid="iou"     kind="iou"     columns="Date  Name  Description  Amount" /></TabsContent>
                  {!isDpdLaser && <TabsContent value="fuel">      <DeductionPanel label="Fuel"              bucket={fuelB}    state={fuelDeducts}  setState={setFuelDeducts}  color="text-blue-600"   testid="fuel"    kind="fuel"    columns="Date  Name  Description  Amount" /></TabsContent>}
                  <TabsContent value="uniform">   <DeductionPanel label="Uniform"           bucket={uniformB} state={uniform}      setState={setUniform}      color="text-purple-600" testid="uniform" kind="uniform" columns="Date  Name  Item  Size  Quantity  Amount  Total" /></TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Footer Note (optional)</CardTitle></CardHeader>
              <CardContent>
                <Textarea value={footerNote} onChange={(e) => setFooterNote(e.target.value)} rows={2} placeholder="e.g. A standard GIT and Insurance Third Party Claim Fee of R399.00 has been deducted from this statement." />
                <p className="text-[11px] text-muted-foreground mt-1">Small italic line at the very bottom — typically the GIT/Insurance disclaimer.</p>
              </CardContent>
            </Card>

            <Card data-testid="notes-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Comments / Updates</span>
                  <span className="text-xs font-normal text-muted-foreground">Shown in a boxed section on the PDF</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder={'Any comments or updates for this driver/period.\nExamples:\n• Loan repayment plan updated — R500/week from next month\n• Uniform replacement ordered — delivery 15th\n• Performance bonus discussed — to be reviewed next statement'}
                  data-testid="notes-textarea"
                />
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: summary + generate */}
          <div className="space-y-4 lg:sticky lg:top-4 self-start">
            <Card className="border-primary/40 bg-primary/5">
              <CardHeader className="pb-3"><CardTitle className="text-base">Statement Summary</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Total Pay Out</span><b>{fmtZAR(totalPayOut)}</b></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total Fuel Rebates</span><b>{fmtZAR(totalFuel)}</b></div>
                <div className="border-t pt-2 mt-2 space-y-1">
                  <div className="flex justify-between text-red-600"><span>GIT</span><span>- {fmtZAR(gitB.total)}</span></div>
                  <div className="flex justify-between text-amber-700"><span>IOU</span><span>- {fmtZAR(iouB.total)}</span></div>
                  <div className="flex justify-between text-blue-600"><span>Fuel deduction</span><span>- {fmtZAR(fuelB.total)}</span></div>
                  <div className="flex justify-between text-purple-600"><span>Uniform</span><span>- {fmtZAR(uniformB.total)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-1"><span>Total Deductions</span><span>- {fmtZAR(totalDeductions)}</span></div>
                </div>
                <div className="flex justify-between border-t pt-3 mt-2 text-base">
                  <span className="font-bold">FINAL AMOUNT DUE</span>
                  <span className="text-2xl font-bold text-emerald-600" data-testid="final-amount">{fmtZAR(finalAmount)}</span>
                </div>
                <Button className="w-full mt-2" onClick={handleGenerate} data-testid="generate-statement-btn">
                  <FilePlus className="h-4 w-4 mr-2" /> Generate Statement
                </Button>
              </CardContent>
            </Card>

            {lastStmt && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader className="pb-3"><CardTitle className="text-base text-emerald-700">✓ Statement Created</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="text-emerald-700 font-mono text-xs">ID: {lastStmt.id}</div>
                  <div className="text-emerald-700">{lastStmt.driver_name} — {lastStmt.period_start} → {lastStmt.period_end}</div>
                  <div className="font-bold text-lg">{fmtZAR(lastStmt.totals?.final_amount)}</div>
                  <Button onClick={() => downloadPDF(lastStmt.id, lastStmt.driver_name)} className="w-full" data-testid="download-pdf-btn">
                    <FileDown className="h-4 w-4 mr-2" /> Download PDF
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm">How it works</CardTitle></CardHeader>
              <CardContent className="text-xs text-muted-foreground space-y-2">
                <p>1. <b>Pick a driver</b> (filter by site if needed).</p>
                <p>2. <b>Paste</b> your manifest screenshot — AI extracts trip rows automatically.</p>
                <p>3. <b>Review / edit</b> trips. Add PODs/Returns counts manually if needed.</p>
                <p>4. <b>Add deductions</b> in the GIT / IOU / Fuel / Uniform tabs.</p>
                <p>5. <b>Generate</b> → download the PDF.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
